import { useShallow } from 'zustand/react/shallow'

import { useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { NodeStep } from '@ui/__generated__/graphql'
import { Chip } from '@ui/components/core'
import { ChevronDownIcon, VerticalMenuIcon } from '@ui/components/icons'
import { Config } from '@ui/stores/pipelinesWorkflow/NodeStepStore'
import { QueryState, useQueryBuilderStore } from '@ui/stores/pipelinesWorkflow/QueryBuilderStore'
import clsx from 'clsx'

import { Step } from './Step'

interface SortableStepProps {
  isDragging: boolean
  step: NodeStep
  inputId: string
  openStepIds: string[]
  openStepMenu: string | null
  toggleStep: (id: string) => void
  toggleStepMenu: (step: NodeStep) => void
  deleteStepFromNode: (step: NodeStep) => void
  index: number
  queryConfig: QueryState
  openQueryBuilder: (stepId: string, inputId: string) => void
  openCodeEditor: () => void
  handleUpdateStepConfig: (stepId: string, config: Partial<Config>) => void
  handleSetSelectedStepOverlay: (show?: boolean) => void
}

export const SortableStep = ({
  isDragging,
  step,
  inputId,
  openStepIds,
  openStepMenu,
  toggleStep,
  toggleStepMenu,
  deleteStepFromNode,
  index,
  queryConfig,
  openQueryBuilder,
  openCodeEditor,
  handleUpdateStepConfig,
  handleSetSelectedStepOverlay,
}: SortableStepProps) => {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id: step.id })

  const stepId = useQueryBuilderStore(useShallow((state) => state.stepId))

  return (
    <div
      ref={setNodeRef}
      key={index}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={clsx('group flex w-full justify-between border-gray-200 bg-white px-4 py-2', {
        'h-auto': openStepIds.includes(step.id),
        'h-16': !openStepIds.includes(step.id),
        'rounded-none border-b': !isDragging,
        'rounded-md border-2': isDragging,
        'z-20': stepId === step.id,
      })}>
      <div className="relative flex w-full flex-col space-y-2.5">
        <div className="relative z-0 flex w-full flex-row justify-between space-x-2">
          <div className="relative flex flex-row space-x-2">
            <div
              className="flex size-5 cursor-pointer items-center justify-center rounded transition-all duration-300 ease-in-out hover:bg-gray-200"
              onClick={(e) => {
                e.preventDefault()
                e.stopPropagation()
                toggleStep(step.id)
              }}>
              <ChevronDownIcon
                className={clsx('z-10 size-4 stroke-gray-900', {
                  'rotate-180': openStepIds.includes(step.id),
                })}
              />
            </div>
            <p className="text-xs font-normal">
              {(step.order ?? 0) + 1}. {step.stepType.type}
            </p>
          </div>

          <VerticalMenuIcon className="size-4 cursor-pointer stroke-gray-900" onClick={() => toggleStepMenu(step)} />
        </div>

        <div className="flex flex-row justify-between">
          <div className="flex flex-row space-x-2">
            <Chip>
              <p className="text-2xs font-medium">Logs</p>
            </Chip>
            <Chip>
              <p className="text-2xs font-medium">Spans</p>
            </Chip>
            {/* <Chip>
            <p className="text-xs font-medium">Metrics</p>
            </Chip> */}
          </div>
        </div>

        {/* Drag Handle */}
        <div
          {...attributes}
          {...listeners}
          className={clsx(
            'absolute -bottom-2 -right-3.5 size-0 cursor-move transition-all duration-300 ease-in-out',
            'border-b-0 border-l-0 border-r-[1.4rem] border-t-[1.4rem] border-solid',
            'border-r-transparent border-t-transparent',
            {
              'border-r-primary-300': isDragging,
              'group-hover:border-r-primary-300': !isDragging,
            }
          )}
        />

        {/* Step Menu */}
        {openStepMenu === step.id && (
          <div className="absolute -top-3 right-4 z-10 rounded-md border border-gray-200 bg-white shadow-md">
            <div className="px-2 py-1 hover:cursor-pointer hover:bg-gray-100" onClick={() => deleteStepFromNode(step)}>
              <p className="text-xs font-normal hover:cursor-pointer hover:text-gray-900">Delete</p>
            </div>
          </div>
        )}

        {openStepIds.includes(step.id) && (
          <Step
            step={step}
            inputId={inputId}
            queryConfig={queryConfig}
            openQueryBuilder={openQueryBuilder}
            openCodeEditor={openCodeEditor}
            handleUpdateStepConfig={handleUpdateStepConfig}
            handleSetSelectedStepOverlay={handleSetSelectedStepOverlay}
          />
        )}
      </div>
    </div>
  )
}
